import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { Header } from '@components/Header';
import DiagonalArrow from '@components/icons/DiagonalArrow.svg';
import { Button } from '@components/core-ui/form-elements/buttons/Button';
import { PagePagination } from '@components/core-ui/Pagination';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { downloadBillingTransactionsCsv } from '@helpers/downloadBillingTransactionsCsv';
import { formatCentsPrice } from '@helpers/formatCentsPrice';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { useAuthContext } from '@providers/AuthProvider';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useListQueryParams } from 'app/hooks/useListQueryParams';
import { BillingResourceError, getBillingResourceError } from 'app/helpers/getBillingResourceError';
import { billingKeys } from 'app/queries/billing/billingKeys';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';

import { createFetchBillingAccountQueryFn } from './api/fetchBillingAccount';
import { createFetchMemberPaymentMethodsQueryFn } from 'app/queries/billing/createFetchMemberPaymentMethodsQueryFn';
import { createFetchBillingAccountTransactionsQueryFn } from './api/fetchBillingAccountTransactions';
import { AutoTopUpCard } from './components/AutoTopUpCard/AutoTopUpCard';
import { CreditBalanceCard } from './components/CreditBalanceCard';
import { PaymentMethodCard } from './components/PaymentMethodCard';
import { SubscriptionsWrapper } from './components/SubscriptionsWrapper';
import { AudioHourRateCard } from './components/SubscriptionsWrapper/Cards/AudioHourRateCard';
import { TransactionType } from './components/TransactionType';
import { CardWrapper } from './styles/CardWrapper';
import { Cell } from './styles/Cell';
import { ReceiptUrl } from './styles/ReceiptUrl';
import { StyledTitle } from './styles/StyledTitle';
import { Wrapper } from './styles/Wrapper';

export { StyledTitle };

const BillingResourceErrorMessage = ({
  error,
  hasLoaded,
  isLoading,
  onRetry,
}: {
  error: BillingResourceError;
  hasLoaded: boolean;
  isLoading: boolean;
  onRetry: () => Promise<void>;
}) => {
  if (error.kind === 'none') {
    return null;
  }

  if (error.kind === 'forbidden') {
    return <p className="mt-4 text-sm text-foreground">Access denied</p>;
  }

  return (
    <div className="mt-4 flex items-center gap-3 text-sm text-foreground" role="alert">
      <span>{hasLoaded ? "Couldn't refresh — showing previously loaded data" : "Couldn't load billing details"}</span>
      <Button disabled={isLoading} onClick={() => void onRetry()}>
        Retry
      </Button>
    </div>
  );
};

export const BillingAccountDetailsPage = () => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const { me, updateMe, organization } = useAuthContext();
  const baId = organization?.billingAccount?.id ?? '';
  const { page, pageSize, setPage, setPageSize } = useListQueryParams({
    defaults: { pageSize: 25, orderBy: 'createdAt', orderType: 'desc' },
  });

  const accountQuery = useQuery({
    queryKey: billingKeys.memberAccount(baId),
    queryFn: createFetchBillingAccountQueryFn(call),
    enabled: baId !== '',
    refetchInterval: 60_000,
  });
  const paymentMethodsQuery = useQuery({
    queryKey: billingKeys.memberPaymentMethodsByAccount(baId),
    queryFn: createFetchMemberPaymentMethodsQueryFn(call),
    enabled: baId !== '',
    refetchInterval: 60_000,
  });
  const transactionsQuery = useQuery({
    queryKey: billingKeys.memberTransactionsByAccount(baId),
    queryFn: createFetchBillingAccountTransactionsQueryFn(call),
    enabled: baId !== '',
    refetchInterval: 60_000,
  });

  const exportMutation = useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        queryKey: billingKeys.memberTransactionsByAccount(baId),
        queryFn: createFetchBillingAccountTransactionsQueryFn(call),
        staleTime: 0,
      }),
    onSuccess: (transactions) => {
      void downloadBillingTransactionsCsv(transactions);
    },
  });

  const billingAccount = accountQuery.data;
  const allPaymentMethods = paymentMethodsQuery.data;
  const paymentMethods = allPaymentMethods
    ? {
        default: allPaymentMethods.find((item) => item.default) || allPaymentMethods[0],
        all: allPaymentMethods,
      }
    : undefined;
  const allTransactions = transactionsQuery.data ?? [];
  const data = {
    count: allTransactions.length,
    data: allTransactions.slice(pageSize * page - pageSize, pageSize * page),
  };

  const [isRetryingDetails, setIsRetryingDetails] = useState(false);
  const detailsError: BillingResourceError =
    accountQuery.isError || paymentMethodsQuery.isError
      ? getBillingResourceError(accountQuery.error ?? paymentMethodsQuery.error)
      : isRetryingDetails
        ? { kind: 'retryable' }
        : { kind: 'none' };
  const transactionsError: BillingResourceError = transactionsQuery.isError
    ? getBillingResourceError(transactionsQuery.error)
    : { kind: 'none' };
  const detailsLoaded = accountQuery.data !== undefined && paymentMethodsQuery.data !== undefined;
  const transactionsLoaded = transactionsQuery.data !== undefined;
  const detailsLoading = accountQuery.isFetching || paymentMethodsQuery.isFetching;
  const transactionsLoading = transactionsQuery.isFetching || exportMutation.isPending;
  const isLoading = detailsLoading || transactionsLoading;

  const refetchDetails = async () => {
    await Promise.all([accountQuery.refetch(), paymentMethodsQuery.refetch()]);
  };

  const retryDetails = async () => {
    setIsRetryingDetails(true);
    try {
      await refetchDetails();
    } finally {
      setIsRetryingDetails(false);
    }
  };

  const loadData = async () => {
    await transactionsQuery.refetch();
  };

  const reloadCards = () => {
    void queryClient.invalidateQueries({ queryKey: billingKeys.memberAccount(baId) });
    void queryClient.invalidateQueries({ queryKey: billingKeys.memberPaymentMethodsByAccount(baId) });
    void queryClient.invalidateQueries({ queryKey: billingKeys.memberTransactionsByAccount(baId) });
  };

  const onExportCsv = () => {
    if (!exportMutation.isPending) {
      exportMutation.mutate();
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      void updateMe();
    }, 60000);
    return () => clearInterval(interval);
  }, [updateMe]);

  const billingAccountOrg = me?.organizations?.find(
    (org) => org.billingAccount?.id === (organization?.billingAccount?.id ?? ''),
  );
  const canManageBillingAccount = !!billingAccountOrg?.permissions?.manageBilling;

  if (!orgSlug || !organization) {
    return (
      <>
        <Helmet>
          <title>Billing Account Details - AutoScript</title>
        </Helmet>
        <Header title="Billing Account Details" />
        <LinearProgressLoader active={isLoading} />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Billing Account Details - AutoScript</title>
      </Helmet>
      <Header
        title="Billing Account Details"
        buttonLabel={data.data?.length ? 'Export CSV' : undefined}
        buttonOnClick={data.data?.length ? () => void onExportCsv() : undefined}
      />

      <LinearProgressLoader active={isLoading} />
      <Wrapper>
        <CardWrapper>
          <BillingResourceErrorMessage
            error={detailsError}
            hasLoaded={detailsLoaded}
            isLoading={detailsLoading}
            onRetry={retryDetails}
          />
          {detailsError.kind === 'none' || (detailsError.kind === 'retryable' && detailsLoaded) ? (
            <>
              <PaymentMethodCard
                method={paymentMethods?.default}
                billingAccount={billingAccount}
                canManageBillingAccount={canManageBillingAccount}
                reloadCards={reloadCards}
              />
              <CreditBalanceCard
                billingAccount={billingAccount}
                canManageBillingAccount={canManageBillingAccount}
                reloadCards={reloadCards}
              />
              {billingAccount && paymentMethods ? (
                <AutoTopUpCard
                  billingAccount={billingAccount}
                  paymentMethods={paymentMethods}
                  reloadData={refetchDetails}
                />
              ) : null}
              <AudioHourRateCard billingAccount={billingAccount} />
            </>
          ) : null}
        </CardWrapper>

        <SubscriptionsWrapper
          billingAccount={billingAccount}
          isAsOneAllowed={!!organization.asOneAllowed}
          isBillingAccountLoading={accountQuery.isLoading}
          reloadPage={reloadCards}
          hasPaymentMethod={!!paymentMethods?.default}
        />

        <StyledTitle className="mt-8">Transactions</StyledTitle>
        <BillingResourceErrorMessage
          error={transactionsError}
          hasLoaded={transactionsLoaded}
          isLoading={transactionsLoading}
          onRetry={loadData}
        />
        {transactionsError.kind === 'none' || (transactionsError.kind === 'retryable' && transactionsLoaded) ? (
          <TableWrapper>
            <Table>
              <Thead>
                <Tr>
                  <Th style={{ width: '170px', maxWidth: '170px' }}>Created</Th>
                  <Th>Title</Th>
                  <Th style={{ width: '90px', maxWidth: '90px' }}>Amount</Th>
                  <Th style={{ width: '90px', maxWidth: '90px' }}>Type</Th>
                  <Th style={{ width: '85px', maxWidth: '85px' }}>Receipt Url</Th>
                </Tr>
              </Thead>
              <Tbody>
                {(data.data || []).map((transaction) => (
                  <Tr key={transaction.id}>
                    <Td>
                      <p className="my-1">{getAmericanTime(transaction.createdAt)}</p>
                    </Td>
                    <Td>
                      <Cell>
                        <p className="my-1 max-w-full text-ellipsis overflow-hidden">
                          {transaction.title} <br />
                          <small className="text-858dbd text-xs">
                            Charge ID: {transaction.stripeChargeId ?? transaction.id}
                          </small>
                        </p>
                      </Cell>
                    </Td>
                    <Td>
                      <p className="my-1">{formatCentsPrice(transaction.amount)}</p>
                    </Td>
                    <Td>
                      <TransactionType className="my-1" type={transaction.code.name} />
                    </Td>
                    <Td>
                      <div className="my-1">
                        {transaction.stripeReceiptUrl ? (
                          <ReceiptUrl href={transaction.stripeReceiptUrl} target="_blank" rel="noreferrer">
                            Receipt{' '}
                            <div className="mt-2 ml-1">
                              <DiagonalArrow />
                            </div>
                          </ReceiptUrl>
                        ) : null}
                      </div>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableWrapper>
        ) : null}
        <PagePagination
          totalItemsCount={data.count ?? 0}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </Wrapper>
    </>
  );
};
